/**
 * The Running system section's wire, `/api/system-environments`.
 *
 * A 404 is the off switch: the server answers 404 on every path unless the
 * workspace runs on Machines (`sandbox.machines_runner`, which the client
 * cannot read), so a read that 404s returns `null` and the section renders
 * nothing at all.
 *
 * The answers post to the needs routes (`routes/systemEnvironments/needs.ts`):
 *
 *   POST .../teams/:teamId/needs/:needId/provide  `{ secret, repoId? }` saves a
 *        value; `{}` marks a need provided that takes no value
 *   POST .../teams/:teamId/needs/:needId/decline  no body
 *   POST .../teams/:teamId/needs/:needId/reopen   no body
 *
 * Each answers `{ need }`, the need's ask with names and statuses only. The
 * section reads the record again after an answer instead, so nothing here
 * parses a response for a value, and a value goes out once and is never kept.
 */
import {
  needTakesValue,
  SYSTEM_NEED_VALUE_MAX_LENGTH,
  SYSTEM_NEED_VALUE_MIN_LENGTH,
  type SystemEnvironmentReadResponse,
  type SystemNeed,
  type SystemRepositoryTeamsResponse,
  type SystemWorkspaceReadResponse,
} from "@yagni/shared";
import { readJsonError } from "../../lib/readJsonError.js";

const BASE = "/api/system-environments";

/**
 * The bounds the provide route enforces on a value. A shorter value is
 * refused because the copy's output redactor leaves short strings alone.
 */
export const NEED_VALUE_MIN_LENGTH = SYSTEM_NEED_VALUE_MIN_LENGTH;
export const NEED_VALUE_MAX_LENGTH = SYSTEM_NEED_VALUE_MAX_LENGTH;

/**
 * How a need is answered, the needs routes' own rule (`needTakesValue`):
 *   enter_value    a credential, or test data named as a variable: type the value
 *   mark_provided  other test data, or access: say it is in place, nothing is stored
 *   none           a capability: YAGNI's to build, there is nothing to enter
 */
export type NeedAnswer = "enter_value" | "mark_provided" | "none";

export function needAnswer(need: Pick<SystemNeed, "kind" | "name">): NeedAnswer {
  if (need.kind === "capability") return "none";
  return needTakesValue(need) ? "enter_value" : "mark_provided";
}

export interface SystemApi {
  readTeam(teamId: string): Promise<SystemEnvironmentReadResponse | null>;
  /** The workspace System for the System tab under Connections; null when it is off. */
  readWorkspace(): Promise<SystemWorkspaceReadResponse | null>;
  readRepositoryTeams(repoId: string): Promise<SystemRepositoryTeamsResponse | null>;
  /** Saves a value for a need that takes one. `repoId` is left out when the need's part names it. */
  provideNeed(
    teamId: string,
    needId: string,
    body: { secret: string; repoId?: string },
  ): Promise<void>;
  /** Marks a need that takes no value as provided. */
  markNeedProvided(teamId: string, needId: string): Promise<void>;
  declineNeed(teamId: string, needId: string): Promise<void>;
  reopenNeed(teamId: string, needId: string): Promise<void>;
}

async function readOrNull<T>(path: string): Promise<T | null> {
  const res = await fetch(path, { credentials: "include" });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await readJsonError(res));
  return (await res.json()) as T;
}

function needPath(teamId: string, needId: string, action: "provide" | "decline" | "reopen") {
  return `${BASE}/teams/${encodeURIComponent(teamId)}/needs/${encodeURIComponent(needId)}/${action}`;
}

/** Posts an answer. The response is not read past its status: it carries no value, and the record is read again. */
async function answer(path: string, body: Record<string, string>): Promise<void> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readJsonError(res));
}

export const systemApi: SystemApi = {
  readTeam: (teamId) => readOrNull(`${BASE}/teams/${encodeURIComponent(teamId)}`),
  readWorkspace: () => readOrNull(`${BASE}/system`),
  readRepositoryTeams: (repoId) => readOrNull(`${BASE}/repositories/${encodeURIComponent(repoId)}`),
  provideNeed: (teamId, needId, body) =>
    answer(
      needPath(teamId, needId, "provide"),
      body.repoId ? { secret: body.secret, repoId: body.repoId } : { secret: body.secret },
    ),
  markNeedProvided: (teamId, needId) => answer(needPath(teamId, needId, "provide"), {}),
  declineNeed: (teamId, needId) => answer(needPath(teamId, needId, "decline"), {}),
  reopenNeed: (teamId, needId) => answer(needPath(teamId, needId, "reopen"), {}),
};
